import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { money, signedMoney } from "./format";

export const MEASURES = [
  { key: "est", label: "Estimate" },
  { key: "bud", label: "Budget" },
  { key: "act", label: "Actual" },
  { key: "inv", label: "Invoiced" },
  { key: "pd", label: "Paid" },
];

const gridSx = (columns) => ({
  display: "grid",
  gap: 1.5,
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    md: `repeat(${columns}, minmax(0, 1fr))`,
  },
});

const numberSx = { fontVariantNumeric: "tabular-nums" };

/** The five headline totals. "—" means no source has fed that measure. */
export function KpiCards({ totals, lineCount }) {
  return (
    <Box sx={gridSx(5)}>
      {MEASURES.map(({ key, label }) => {
        const has = totals.has[key];
        return (
          <Paper key={key} variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="overline" color="text.secondary">
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{ ...numberSx, color: has ? "text.primary" : "text.disabled" }}
            >
              {has ? money(totals[key]) : "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {key === "est"
                ? `${lineCount} WBS lines`
                : has
                  ? "linked"
                  : "no data"}
            </Typography>
          </Paper>
        );
      })}
    </Box>
  );
}

function VarianceCard({ label, result, goodWhen }) {
  const value = result?.value ?? null;
  const good = goodWhen === "gte" ? value >= 0 : value <= 0;

  return (
    <Paper variant="outlined" sx={{ p: 1.25, bgcolor: "action.hover" }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography
        variant="body1"
        fontWeight={700}
        sx={{
          ...numberSx,
          color:
            value == null
              ? "text.disabled"
              : good
                ? "success.main"
                : "error.main",
        }}
      >
        {value == null
          ? "—"
          : goodWhen === "gte"
            ? signedMoney(value)
            : money(value)}
      </Typography>
      {result && (
        <Typography variant="caption" color="text.secondary">
          on {result.scope.join(", ")}
        </Typography>
      )}
    </Paper>
  );
}

/**
 * Each variance only covers the categories where both of its sides exist, and
 * says which - so procurement actuals are never set against the whole estimate.
 */
export function VarianceCards({ vars }) {
  return (
    <Box sx={gridSx(4)}>
      <VarianceCard
        label="Estimate accuracy · Est − Actual"
        result={vars.accuracy}
        goodWhen="gte"
      />
      <VarianceCard
        label="Budget performance · Budget − Actual"
        result={vars.budgetPerformance}
        goodWhen="gte"
      />
      <VarianceCard
        label="Margin · Invoiced − Actual"
        result={vars.margin}
        goodWhen="gte"
      />
      <VarianceCard
        label="Receivable · Invoiced − Paid"
        result={vars.receivable}
        goodWhen="lte"
      />
    </Box>
  );
}
